import { FC, ReactNode, useMemo } from 'react';
import { useNumericFormat } from 'react-number-format';
import cx from 'classnames';
import { paymentSelectors } from '@/redux/selectors';
import { useAppSelector } from '@/redux/hooks';
import {
  calculateSavePercentage,
  getPlanOptionTranslation,
  getPrice,
} from '@/helpers';
import parse from 'html-react-parser';
import { TariffsDetailsToggler } from '@/components';
import { useTranslation } from 'react-i18next';
import { Plan } from '@/api';

import { SaveBadge } from '@/components/SaveBadge';
import { TariffType } from '@/config';
import { INDIVIDUAL_PRO_PLUS_PLAN_VALUE } from '@/pages/Tariffs';

import styles from './TariffsDetails.module.sass';

interface TariffsDetailsProps {
  tariff: Plan;
  tariffPrices: Record<string, any>;
  description?: string;
  children?: ReactNode;
  showPaymentTitle?: boolean;
  planCategory?: string;
}

const TariffsDetails: FC<TariffsDetailsProps> = ({
  tariff,
  tariffPrices,
  description,
  children,
  planCategory = 'lite',
  showPaymentTitle = true,
}) => {
  const { t } = useTranslation();
  const classes = cx(styles.details, `${styles['has-discount']}`);
  const { biggestPricePerCheck, paymentPlans } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );
  // TODO: Refactor to pluralize
  const checksText = () => {
    if (Number(tariff.value) === 1) {
      return t('translation.checks_without_count_singular');
    }

    return t('translation.checks_without_count_plural');
  };

  const numberFormat = useNumericFormat({
    thousandSeparator: ',',
    decimalScale: 2,
  });

  const { price, pricePerCheck, oldPrice, discount } = getPrice(
    tariff.price,
    tariff.value,
    tariff.old_price,
    0,
  );

  const savePercentage = useMemo(() => {
    return calculateSavePercentage(
      pricePerCheck,
      biggestPricePerCheck,
      Number(paymentPlans?.baseUsdPrice),
    );
  }, [pricePerCheck, biggestPricePerCheck]);

  const formattedNumberOfChecks = useMemo(() => {
    if (tariff.value === INDIVIDUAL_PRO_PLUS_PLAN_VALUE) {
      return tariff.value;
    }

    return numberFormat.format?.((Number(tariff.value) || 0).toString());
  }, [tariff.value]);

  return (
    <div className={classes}>
      <div className={styles.info}>
        <div className={styles.checks}>
          <span>{formattedNumberOfChecks}</span> {checksText()}
        </div>

        {!!description && (
          <div className={styles.description}>{parse(description)}</div>
        )}

        {!!tariffPrices.price && (
          <div className={styles.priceWrap}>
            <div className={styles.price}>
              $
              {numberFormat.format?.(
                (Number(tariffPrices.price) || 0).toString(),
              )}
            </div>
            {!!tariffPrices.discount && (
              <div className={styles.discount}>
                -
                {numberFormat.format?.(
                  (Number(tariffPrices.discount) || 0).toString(),
                )}
                %
              </div>
            )}
            {!!savePercentage && !tariffPrices.discount && (
              <SaveBadge
                tariff={planCategory as TariffType}
                value={savePercentage}
                isActive
              />
            )}
          </div>
        )}

        {!!oldPrice && (!!discount || !!tariffPrices.discount) && (
          <div className={styles.oldPrice}>
            ${numberFormat.format?.(oldPrice.toString())}
          </div>
        )}

        {!!tariffPrices.pricePerCheck && (
          <div className={styles.pricePerCheck}>
            $
            {t('translation.invoices_price_str', {
              count: tariffPrices.pricePerCheck,
            })}
          </div>
        )}
      </div>

      {showPaymentTitle && (
        <div className={styles.payment__head}>
          <div className={styles.payment__title}>
            {t('translation.payment_title')}
          </div>
          <div className={styles.payment__line} />
        </div>
      )}

      <div className={styles.payment__body}>{children}</div>

      {tariff.options?.length > 0 && (
        <TariffsDetailsToggler title={t('translation.advantages')} isOpen>
          <div className={styles.advantages}>
            {tariff.options.map((option: string) => {
              const { isActive, translation, withContent } =
                getPlanOptionTranslation(option);
              return (
                <div
                  className={`${styles.advantages__item} ${
                    isActive ? '' : styles.advantages__item_disabled
                  }`}
                  key={translation}
                >
                  {withContent
                    ? t(translation, { count: Number(tariff.value) })
                    : t(translation)}
                </div>
              );
            })}
          </div>
        </TariffsDetailsToggler>
      )}
    </div>
  );
};

export default TariffsDetails;
